import React, { useContext, useEffect, useState } from "react";
import PageLayout from "../../PageLayout";
import { EPageTitle } from "../../libs/ui/src/Pagetitle/EPageTitle";
import Chart from "react-apexcharts";
import { EStat } from "../../libs/ui/src/Stat/EStat";
import { Loader, Grid, Paper, Text, Group, Stack } from "@mantine/core";
import { AppContext } from "../../AppContext";
import useAnalytics from "../../hooks/useAnalytics";
import { EPatientList } from "../../libs/ui";
import EPendingVisitList from "./components/EPendingVisitList";

export default function AnalyticsDashboard() {

    const { loading,
        totalStats,
        centerWiseStats,
        visitCompletedByCenter,
        maxNoOfVisits,
        surgeryBreakdown,
    } = useAnalytics();

    const { role } = useContext(AppContext);


    if (loading) {
        return (
            <PageLayout header={<EPageTitle title="Analytics Dashboard" />}>
                <div className="flex justify-center items-center h-[60vh]">
                    <Loader size="xl" />
                </div>
            </PageLayout>
        );
    }

    // Prepare data for Charts
    const centerNames = centerWiseStats.map((c) => c.center_name);

    // 2. Disease Distribution (Global)
    const diseaseDistributionSeries = [
        Number(totalStats?.symptomatic_gallstone_patients ?? 0),
        Number(totalStats?.asymptomatic_gallstone_patients ?? 0),
        Number(totalStats?.gallbladder_cancer_patients ?? 0),
        Number(totalStats?.non_gallstone_control_patients ?? 0),
    ];

    const diseaseDistributionOptions: any = {
        labels: [
            "SGS",
            "AGS",
            "GBC",
            "NGC",
        ],
        colors: ["#3b82f6", "#10b981", "#ef4444", "#f59e0b"],
        chart: {
            type: "donut",
            animations: {
                enabled: true,
                speed: 800,
                animateGradually: {
                    enabled: true,
                    delay: 150
                },
                dynamicAnimation: {
                    enabled: true,
                    speed: 350
                }
            }
        },
        legend: {
            position: "bottom",
            fontSize: '12px',
            markers: { radius: 12 },
            itemMargin: { horizontal: 5, vertical: 5 }
        },
        title: {
            text: "Global Disease Distribution",
            align: "center",
            style: { fontSize: '16px', fontWeight: 'bold', color: '#1e293b' }
        },
        plotOptions: {
            pie: {
                donut: {
                    size: '65%',
                    labels: {
                        show: true,
                        total: {
                            show: true,
                            label: 'Total',
                            formatter: () => totalStats?.total_patients || 0
                        }
                    }
                }
            }
        },
        tooltip: {
            y: {
                formatter: (val: number) => `${val} Patients`
            }
        }
    };

    // 3. Disease Breakdown by Center (Stacked)
    const diseaseBreakdownSeries = [
        {
            name: "SGS",
            data: centerWiseStats.map((c) => Number(c.sgs)),
        },
        {
            name: "AGS",
            data: centerWiseStats.map((c) => Number(c.ags)),
        },
        {
            name: "GBC",
            data: centerWiseStats.map((c) => Number(c.gbc)),
        },
        {
            name: "NGC",
            data: centerWiseStats.map((c) => Number(c.ngc)),
        },
    ];

    const diseaseBreakdownOptions: any = {
        chart: {
            type: "bar",
            stacked: true,
            toolbar: { show: false },
        },
        xaxis: {
            categories: centerNames,
            labels: {
                rotate: -45,
                style: { fontSize: '10px' }
            }
        },
        legend: { position: "top" },
        colors: ["#3b82f6", "#10b981", "#ef4444", "#f59e0b"],
        title: {
            text: "Disease Breakdown by Center",
            align: "left",
            style: { fontSize: '16px', fontWeight: 'bold', color: '#1e293b' }
        },
    };

    // 4. Surgery Done Breakdown by Center
    const surgeryCenterNames = surgeryBreakdown.map((c) => c.center_name);

    const surgeryBreakdownSeries = [
        {
            name: "Surgery Done",
            data: surgeryBreakdown.map((c) => c.surgery_done),
        },
        {
            name: "Surgery Not Done",
            data: surgeryBreakdown.map((c) => c.surgery_not_done),
        },
    ];

    const surgeryBreakdownOptions: any = {
        chart: {
            type: "bar",
            stacked: false,
            toolbar: { show: false },
            animations: {
                enabled: true,
                speed: 800,
                animateGradually: { enabled: true, delay: 100 },
                dynamicAnimation: { enabled: true, speed: 350 },
            },
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: "55%",
                borderRadius: 4,
            },
        },
        dataLabels: { enabled: false },
        xaxis: {
            categories: surgeryCenterNames,
            labels: {
                rotate: -45,
                style: { fontSize: '10px', colors: '#6b7280' },
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            title: {
                text: "No. of Patients",
                style: { fontSize: "12px", fontWeight: 600, color: "#6b7280" },
            },
            labels: { style: { fontSize: "11px", colors: "#6b7280" } },
            min: 0,
        },
        legend: {
            position: "top",
            horizontalAlign: "left",
            fontSize: "12px",
            markers: { radius: 12 },
            itemMargin: { horizontal: 8, vertical: 4 },
        },
        colors: ["#10b981", "#f43f5e"],
        grid: {
            borderColor: "#e2e8f0",
            strokeDashArray: 4,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
        },
        tooltip: {
            shared: true,
            intersect: false,
            y: {
                formatter: (val: number) => `${val} Patient${val !== 1 ? "s" : ""}`,
            },
        },
        title: {
            text: "Surgery Done Breakdown by Center",
            align: "left",
            style: { fontSize: '16px', fontWeight: 'bold', color: '#1e293b' },
        },
    };


    // 3. Visit Completed Breakdown by Center — each center is a line, visits are on X-axis
    const visitCenters = visitCompletedByCenter?.centers ?? [];
    const visitCenterNames = visitCenters.map((c) => c.center_name);
    const visitLabels = Array.from({ length: maxNoOfVisits }, (_, i) => i === 0 ? `BaseLine` : `Visit ${i}`);

    // Each series = one center; data points = patient count per visit
    const visitCompletedSeries = visitCenters.map((center) => ({
        name: center.center_name,
        data: Array.from({ length: maxNoOfVisits }, (_, i) => {
            const entry = center.visits.find((v) => v.visit_number === i + 1);
            return entry ? Number(entry.total_patients) : 0;
        }),
    }));

    const visitCompletedOptions: any = {
        chart: {
            type: "line",
            toolbar: { show: false },
            animations: {
                enabled: true,
                easing: "easeinout",
                speed: 700,
                animateGradually: { enabled: true, delay: 100 },
            },
            zoom: { enabled: false },
        },
        stroke: {
            curve: "smooth",
            width: 2.5,
        },
        markers: {
            size: 5,
            strokeWidth: 2,
            strokeColors: "#fff",
            hover: { size: 7 },
        },
        dataLabels: { enabled: false },
        // X-axis: Visit 1, Visit 2 ... Visit N
        xaxis: {
            categories: visitLabels,
            labels: {
                style: { fontSize: "12px", colors: "#6b7280", fontWeight: 500 },
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
            title: {
                text: "Visit Number",
                style: { fontSize: "12px", fontWeight: 600, color: "#6b7280" },
            },
        },
        // Y-axis: No. of Patients
        yaxis: {
            title: {
                text: "No. of Patients",
                style: { fontSize: "12px", fontWeight: 600, color: "#6b7280" },
            },
            labels: {
                style: { fontSize: "11px", colors: "#6b7280" },
            },
            min: 0,
        },
        legend: {
            position: "top",
            horizontalAlign: "center",
            fontSize: "11px",
            markers: { radius: 12, width: 10, height: 10 },
            itemMargin: { horizontal: 6, vertical: 4 },
        },
        tooltip: {
            shared: true,
            intersect: false,
            x: { show: true },
            y: {
                formatter: (val: number) => `${val} Patient${val !== 1 ? "s" : ""}`,
            },
        },
        grid: {
            borderColor: "#e2e8f0",
            strokeDashArray: 4,
            xaxis: { lines: { show: false } },
            yaxis: { lines: { show: true } },
        },
        title: {
            text: "No. of Visits Completed by Patients",
            align: "left",
            style: { fontSize: "16px", fontWeight: "700", color: "#1e293b" },
        },
        fill: {
            type: "gradient",
            gradient: {
                shadeIntensity: 1,
                opacityFrom: 0.2,
                opacityTo: 0.0,
                stops: [0, 100],
            },
        },
    };


    return (
        <PageLayout
            header={
                <EPageTitle
                    title="Comparative Analytics"
                    description="Visual comparison of patient data across all participating centers"
                />
            }
        >
            <div className="analytics-container space-y-6 pb-10">
                {/* Top Metric Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                    <EStat
                        stat={totalStats?.symptomatic_gallstone_patients ?? 0}
                        label="Symptomatic Gallstone"
                        text="SGS"
                        hideDownload
                    />
                    <EStat
                        stat={totalStats?.asymptomatic_gallstone_patients ?? 0}
                        label="Asymptomatic Gallstone"
                        text="AGS"
                        hideDownload
                    />
                    <EStat
                        stat={totalStats?.gallbladder_cancer_patients ?? 0}
                        label="Gallbladder Cancer"
                        text="GBC"
                        hideDownload
                    />
                    <EStat
                        stat={totalStats?.non_gallstone_control_patients ?? 0}
                        label="Non Gallstone Control"
                        text="NGC"
                        hideDownload
                    />
                    <EStat
                        stat={totalStats?.total_patients ?? 0}
                        label="Total Participants"
                        text="Total"
                        hideDownload
                    />
                </div>

                {/* Target follow up list */}
                <EPendingVisitList />

                {/* Charts Row 1 */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="bg-white/60 backdrop-blur-md p-6 col-span-2 rounded-2xl border border-white/40 shadow-xl overflow-hidden">
                        <Chart
                            options={visitCompletedOptions}
                            series={visitCompletedSeries}
                            type="line"
                            height={400}
                        />
                    </div>
                    <div className="bg-white/60 backdrop-blur-md p-6 rounded-2xl border border-white/40 shadow-xl overflow-hidden">
                        <Chart
                            key={`donut-${diseaseDistributionSeries.join("-")}`}
                            options={diseaseDistributionOptions}
                            series={diseaseDistributionSeries}
                            type="donut"
                            height={350}
                        />
                    </div>
                </div>

                {/* Charts Row 2 - Disease Breakdown */}
                <div className="grid grid-cols-2 gap-6">
                    <div className="bg-white/60 backdrop-blur-md p-6 rounded-2xl border border-white/40 shadow-xl overflow-hidden">
                        <Chart
                            options={diseaseBreakdownOptions}
                            series={diseaseBreakdownSeries}
                            type="bar"
                            height={400}
                        />
                    </div>
                    <div className="bg-white/60 backdrop-blur-md p-6 rounded-2xl border border-white/40 shadow-xl overflow-hidden">
                        <Chart
                            options={surgeryBreakdownOptions}
                            series={surgeryBreakdownSeries}
                            type="bar"
                            height={400}
                        />
                    </div>

                </div>

                {/* Center Wise Table Summary */}
                <div className="bg-white/60 backdrop-blur-md p-6 rounded-2xl border border-white/40 shadow-xl">
                    <Text fw={700} fz="lg" mb="md">Center-wise Detailed Statistics</Text>
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-gray-200">
                                    <th className="py-3 px-4 font-semibold text-gray-700">Center Name</th>
                                    <th className="py-3 px-4 font-semibold text-gray-700">SGS</th>
                                    <th className="py-3 px-4 font-semibold text-gray-700">AGS</th>
                                    <th className="py-3 px-4 font-semibold text-gray-700">GBC</th>
                                    <th className="py-3 px-4 font-semibold text-gray-700">NGC</th>
                                    <th className="py-3 px-4 font-semibold text-gray-700">Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {centerWiseStats.map((center, index) => (
                                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">
                                        <td className="py-3 px-4 text-gray-800">{center.center_name + " (" + center.center_state + ")"}</td>
                                        <td className="py-3 px-4 text-blue-600 font-medium">{center.sgs}</td>
                                        <td className="py-3 px-4 text-green-600 font-medium">{center.ags}</td>
                                        <td className="py-3 px-4 text-red-600 font-medium">{center.gbc}</td>
                                        <td className="py-3 px-4 text-yellow-600 font-medium">{center.ngc}</td>
                                        <td className="py-3 px-4 font-bold">{center.total}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </PageLayout>
    );
}

